import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import healthApi from '@/api/healthApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Brain, Sparkles, AlertCircle, AlertTriangle, Info, CheckCircle2, Crown, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import UpgradeModal from '@/components/UpgradeModal';
import PremiumBadge from '@/components/PremiumBadge';
import QuickFeedback from '@/components/QuickFeedback';
import LifestylePatternsCard from '@/components/LifestylePatternsCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============= SAFE FORMATTER UTILITIES =============
/**
 * Safely converts any value to a displayable string
 * Prevents "Objects are not valid as a React child" errors
 */
const safeString = (value, fallback = 'No details available') => {
  // If already a string, return it
  if (typeof value === 'string') return value || fallback;
  
  // If number or boolean, convert to string
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  
  // If null or undefined, return fallback
  if (value === null || value === undefined) return fallback;
  
  // If object, try to extract common message fields
  if (typeof value === 'object') {
    // Check for common error/message fields
    if (value.msg) return safeString(value.msg, fallback);
    if (value.message) return safeString(value.message, fallback);
    if (value.text) return safeString(value.text, fallback);
    if (value.content) return safeString(value.content, fallback);
    if (value.description) return safeString(value.description, fallback);
    
    // If it's an array, join with commas
    if (Array.isArray(value)) {
      return value.map(item => safeString(item, '')).filter(Boolean).join(', ') || fallback;
    }
    
    // Last resort: return fallback
    console.warn('Cannot safely convert object to string:', value);
    return fallback;
  }
  
  // Unknown type, return fallback
  return fallback;
};

/**
 * Safely extracts an array from API response
 */
const safeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value?.items && Array.isArray(value.items)) return value.items;
  if (value?.data && Array.isArray(value.data)) return value.data;
  return [];
};

/**
 * Validates if an object has the minimum required fields
 */
const isValidInsight = (item) => {
  if (!item || typeof item !== 'object') return false;
  return !!(item.title || item.msg || item.message || item.content);
};

const Insights = () => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [ruleBasedInsights, setRuleBasedInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    fetchData();
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/subscriptions/status`, {
        withCredentials: true
      });
      setSubscriptionStatus(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [insightsRes, recsRes, ruleBasedRes] = await Promise.all([
        healthApi.getInsights().catch(err => ({ data: [] })),
        healthApi.getRecommendations().catch(err => ({ data: [] })),
        healthApi.getRuleBasedInsights().catch(err => ({ data: { insights: [] } }))
      ]);
      
      // Safely extract arrays from responses
      const insightsData = safeArray(insightsRes.data);
      const recsData = safeArray(recsRes.data);
      const ruleBasedData = safeArray(ruleBasedRes.data?.insights || ruleBasedRes.data);
      
      // Filter out invalid items
      const validInsights = insightsData.filter(isValidInsight);
      const validRecs = recsData.filter(isValidInsight);
      const validRuleBased = ruleBasedData.filter(isValidInsight);
      
      setInsights(validInsights);
      setRecommendations(validRecs);
      setRuleBasedInsights(validRuleBased);
    } catch (error) {
      console.error('Error fetching insights:', error);
      // Always set to empty arrays on error
      setInsights([]);
      setRecommendations([]);
      setRuleBasedInsights([]);
      toast.error('Unable to load insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInsights = async () => {
    setGenerating(true);
    try {
      const { data } = await healthApi.generateInsights();
      const count = data?.count || 0;
      toast.success(`Generated ${count} new insights!`);
      fetchData();
    } catch (error) {
      console.error('Generate insights error:', error);
      const errorMsg = safeString(error.response?.data?.detail || error.response?.data, 'Unable to generate insights right now. Please try again.');
      toast.error(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateRecommendations = async () => {
    setGenerating(true);
    try {
      const { data } = await healthApi.generateRecommendations();
      const count = data?.count || 0;
      toast.success(`Generated ${count} new recommendations!`);
      fetchData();
    } catch (error) {
      console.error('Generate recommendations error:', error);
      const errorMsg = safeString(error.response?.data?.detail || error.response?.data, 'Unable to generate recommendations right now. Please try again.');
      toast.error(errorMsg);
    } finally {
      setGenerating(false);
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      case 'medium':
        return <Info className="h-5 w-5 text-yellow-600" />;
      case 'low':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      default:
        return <Info className="h-5 w-5 text-gray-600" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      lifestyle: 'bg-blue-100 text-blue-800',
      nutrition: 'bg-orange-100 text-orange-800',
      exercise: 'bg-green-100 text-green-800',
      sleep: 'bg-purple-100 text-purple-800',
      stress: 'bg-pink-100 text-pink-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4" data-testid="insights-page">
      <div className="max-w-6xl mx-auto pt-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Brain className="h-8 w-8 mr-3 text-purple-600" />
              Wellness Intelligence
            </h1>
            <p className="text-gray-600 mt-2">Preventive health insights and personalized proactive care guidance</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleGenerateInsights}
              disabled={generating}
              data-testid="generate-insights-button"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {generating ? 'Generating...' : 'Generate Insights'}
            </Button>
            <Button
              onClick={handleGenerateRecommendations}
              disabled={generating}
              variant="outline"
              data-testid="generate-recommendations-button"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {generating ? 'Generating...' : 'Get Recommendations'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Health Insights */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Health Insights</h2>
              {/* Lifestyle patterns — observational, hidden until ≥7 check-ins.
                  Renders BEFORE the legacy insights so beta testers see the
                  new tracking-driven observations first. */}
              <LifestylePatternsCard />
              {insights.length === 0 && ruleBasedInsights.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2 font-medium">Complete a check-in to generate your first insights</p>
                    <p className="text-sm text-gray-500 mb-4">
                      We'll analyze your wellness patterns and provide personalized preventive health insights
                    </p>
                    <Button onClick={handleGenerateInsights} disabled={generating} variant="outline">
                      <Sparkles className="h-4 w-4 mr-2" />
                      Or Generate AI Insights Now
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Show rule-based insights first (automatically generated from check-ins) */}
                  {ruleBasedInsights.length > 0 && (
                    <div className="space-y-4" data-testid="rule-based-insights-list">
                      {ruleBasedInsights.map((insight, index) => {
                        // Use safeString for ALL displayed fields
                        const title = safeString(insight.title || insight.msg, 'Health Alert');
                        const message = safeString(insight.message || insight.content || insight.description, 'No details available');
                        const priority = safeString(insight.priority, 'low');
                        const tips = safeArray(insight.actionable_tips);
                        
                        return (
                          <Card key={index} className="border-l-4 border-l-orange-400 bg-orange-50/50">
                            <CardHeader>
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="flex items-center gap-2 mb-2">
                                    {priority === 'high' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                                    <CardTitle className="text-lg">{title}</CardTitle>
                                    <Badge className={priority === 'high' ? 'bg-red-500' : priority === 'medium' ? 'bg-yellow-500' : 'bg-blue-500'}>
                                      {priority.toUpperCase()}
                                    </Badge>
                                  </div>
                                  <CardDescription className="text-base text-gray-700">
                                    {message}
                                  </CardDescription>
                                </div>
                              </div>
                            </CardHeader>
                            {tips.length > 0 && (
                              <CardContent>
                                <div className="bg-white rounded-lg p-4">
                                  <p className="font-semibold text-sm mb-2">💡 What you can do:</p>
                                  <ul className="space-y-1">
                                    {tips.map((tip, tipIndex) => {
                                      const tipText = safeString(tip, 'Tip unavailable');
                                      return (
                                        <li key={tipIndex} className="text-sm flex items-start">
                                          <span className="mr-2">•</span>
                                          <span>{tipText}</span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                                {/* Quick Feedback */}
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                  <QuickFeedback 
                                    itemId={`rule-insight-${index}`}
                                    page="insights"
                                    itemType="rule_based_insight"
                                  />
                                </div>
                              </CardContent>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Show AI-generated insights if available */}
                  {insights.length > 0 && (
                    <div className="space-y-4 mt-4" data-testid="insights-list">
                      {insights.map((insight, idx) => {
                        // Use safeString for ALL displayed fields
                        const title = safeString(insight.title || insight.msg, 'Insight');
                        const content = safeString(insight.content || insight.message || insight.description, 'No details available');
                        const priority = safeString(insight.priority, 'low');
                        const date = safeString(insight.date || insight.created_at, new Date().toLocaleDateString());
                        const insightId = insight.id || `insight-${idx}`;
                        
                        return (
                          <Card key={insightId} className="hover:shadow-lg transition-shadow">
                            <CardHeader>
                              <div className="flex justify-between items-start">
                                <div className="flex items-start space-x-2">
                                  {getPriorityIcon(priority)}
                                  <CardTitle className="text-lg">{title}</CardTitle>
                                </div>
                                <Badge className={getPriorityColor(priority)}>
                                  {priority}
                                </Badge>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <p className="text-gray-700 leading-relaxed">{content}</p>
                              <p className="text-xs text-gray-500 mt-3">Generated on {date}</p>
                              {/* Quick Feedback */}
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <QuickFeedback 
                                  itemId={insightId}
                                  page="insights"
                                  itemType="ai_insight"
                                />
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Recommendations */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Personalized Recommendations</h2>
              {recommendations.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Sparkles className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No recommendations yet</p>
                    <Button onClick={handleGenerateRecommendations} disabled={generating}>
                      Get Personalized Recommendations
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4" data-testid="recommendations-list">
                  {recommendations.map((rec, idx) => {
                    // Use safeString for ALL displayed fields
                    const title = safeString(rec.title || rec.msg, 'Recommendation');
                    const description = safeString(rec.description || rec.content || rec.message, 'No details available');
                    const category = safeString(rec.category, 'general');
                    const recId = rec.id || `rec-${idx}`;
                    
                    // Safely extract action steps
                    const steps = safeArray(rec.actionable_steps);
                    
                    return (
                      <Card key={recId} className="hover:shadow-lg transition-shadow">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <CardTitle className="text-lg">{title}</CardTitle>
                            <Badge className={getCategoryColor(category)}>
                              {category}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-700 mb-4">{description}</p>
                          {steps.length > 0 && (
                            <div className="mt-4">
                              <p className="text-sm font-semibold mb-2">Action Steps:</p>
                              <ul className="space-y-1">
                                {steps.map((step, stepIdx) => {
                                  // Use safeString for each step
                                  const stepText = safeString(step, 'Action step unavailable');
                                  return (
                                    <li key={stepIdx} className="text-sm text-gray-600 flex items-start">
                                      <span className="mr-2">•</span>
                                      <span>{stepText}</span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          )}
                          {/* Quick Feedback */}
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <QuickFeedback 
                              itemId={recId}
                              page="insights"
                              itemType="recommendation"
                            />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="advanced health analytics"
        benefits={[
          'AI-powered predictive health insights',
          'Extended trend analysis (90+ days)',
          'Personalized health recommendations',
          'Export and share your insights',
          'Priority access to new features'
        ]}
      />
    </div>
  );
};

export default Insights;
